/**
 * Stage F — Instrumentation & Control panel.
 *
 * Sub-stage routing follows the project's authoritative
 * `stages_catalog.py` numbering:
 *   F.1 · Control System Architecture     → F1Architecture
 *   F.2 · Instrument Data Sheets (Index)   → F2Instruments
 *   F.3 · Control & Logic (C&E + Logic)    → F3CeLogic
 *   F.4 · Loop Diagrams                    → F4Loops
 *   F.5 · Hookup Drawings & Layouts        → F5Hookups
 *   F.6 · Instrument MTO                   → F6MTO
 *
 * All routes hit /api/projects/{id}/stage-f/*.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

const _fmt = (n, d = 1) => (n == null ? "-"
    : typeof n === "number" ? n.toFixed(d) : n);

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/60 p-3"
          data-testid={testid}>
      <div className="text-2xl font-bold text-neutral-100">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

function Section({ title, children, right, testid }) {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4"
          data-testid={testid}>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold text-neutral-200">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Table({ cols, rows, testid }) {
  return (
    <div className="overflow-x-auto rounded border border-neutral-800"
          data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-neutral-900 text-neutral-400">
          <tr>{cols.map(([, l]) => <th key={l} className="px-2 py-2 text-left font-medium">{l}</th>)}</tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i} className="border-t border-neutral-800 text-neutral-200">
              {cols.map(([k]) => <td key={k} className="px-2 py-1.5">{_fmt(r?.[k])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

async function _openSvg(projectId, path, payload) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: "image/svg+xml" });
  window.open(URL.createObjectURL(blob), "_blank");
}

async function _downloadDxf(projectId, path, payload, filename) {
  const r = await api.post(`/projects/${projectId}${path}`, payload || {});
  const blob = new Blob([r.data], { type: "application/dxf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
}

/* ═══════════════════ F.1 · Control System Architecture ═══════════ */
function F1Architecture({ projectId, res }) {
  const arch = res?.architecture || {};
  const io = res?.io_summary || {};
  const alarms = res?.alarm_summary || {};
  const spec = res?.control_spec_summary || {};
  return (
    <div className="space-y-4" data-testid="f1-architecture">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="System"          value={arch.system_recommendation || "-"} testid="f1-tile-system" />
        <StatTile label="DCS cabinets"    value={arch.cabinets_DCS ?? "-"} testid="f1-tile-dcs" />
        <StatTile label="SIS cabinets"    value={arch.cabinets_SIS ?? "-"} testid="f1-tile-sis" />
        <StatTile label="Operator stations" value={arch.operator_stations ?? "-"} testid="f1-tile-ops" />
      </div>

      <Section title="I/O count summary" testid="f1-io-summary">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="DCS AI" value={io.DCS?.AI ?? 0} />
          <StatTile label="DCS AO" value={io.DCS?.AO ?? 0} />
          <StatTile label="DCS DI" value={io.DCS?.DI ?? 0} />
          <StatTile label="DCS DO" value={io.DCS?.DO ?? 0} />
          <StatTile label="SIS AI" value={io.SIS?.AI ?? 0} />
          <StatTile label="SIS DI" value={io.SIS?.DI ?? 0} />
          <StatTile label="SIS DO" value={io.SIS?.DO ?? 0} />
          <StatTile label="HART loops" value={io.hart_loops ?? 0} />
        </div>
      </Section>

      <Section title="Alarm rationalization (ISA 18.2 / IEC 62682)" testid="f1-alarms">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Total alarms: <b>{alarms.count ?? 0}</b></div>
          <div>Distribution: {JSON.stringify(alarms.distribution || {})}</div>
          <div>Compliance: {alarms.compliance
              ? Object.entries(alarms.compliance).map(([k, v]) =>
                  <span key={k} className={v ? "text-emerald-400 mr-3" : "text-amber-300 mr-3"}>
                    {k}: {v ? "PASS" : "REVIEW"}
                  </span>)
              : "-"}</div>
        </div>
      </Section>

      <Section title="Control system specification (extract)" testid="f1-spec">
        <ul className="text-xs text-neutral-300 space-y-1 list-disc pl-4">
          <li>SIS standard: <b>{arch.sis_standard || "IEC 61511"}</b></li>
          <li>Cybersecurity: <b>{arch.cybersecurity_standard || "IEC 62443"}</b></li>
          <li>Controller: <b>{arch.controller_redundancy}</b></li>
          <li>Network: <b>{arch.network}</b></li>
          <li>Marshalling: <b>{arch.marshalling}</b></li>
          {Object.entries(spec).slice(0, 3).map(([k, v]) =>
            <li key={k}>{k}: <span className="text-neutral-400">{typeof v === "string" ? v.slice(0, 100) : JSON.stringify(v).slice(0, 100)}</span></li>
          )}
        </ul>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs"
                  data-testid="f1-arch-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/architecture/svg", { architecture: arch })}>
          Preview architecture SVG
        </button>
        <button className="btn btn-secondary text-xs"
                  data-testid="f1-arch-dxf-btn"
                  onClick={() => _downloadDxf(projectId,
                    "/stage-f/architecture/dxf",
                    { architecture: arch }, "control-arch.dxf")}>
          Download architecture DXF
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ F.2 · Instrument Index ══════════════════════ */
function F2Instruments({ projectId, res }) {
  const instruments = res?.instrument_index_first20 || [];
  const cables = res?.cable_schedule_first10 || [];
  const jbs = res?.jb_schedule || [];
  const ds = res?.datasheet_sample || {};
  return (
    <div className="space-y-4" data-testid="f2-instruments">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Instruments (total)" value={res?.instrument_index_total ?? 0} testid="f2-tile-total" />
        <StatTile label="Cables (total)"      value={res?.cable_schedule_total ?? 0} testid="f2-tile-cables" />
        <StatTile label="JB count"             value={jbs.length} testid="f2-tile-jbs" />
        <StatTile label="Datasheet type"       value={ds.type ? ds.type.slice(0, 12) : "-"} testid="f2-tile-datasheet" />
      </div>

      <Section title="Instrument Index (first 20)" testid="f2-index-table">
        <Table cols={[
          ["tag", "Tag"], ["measured_variable", "Var"],
          ["range", "Range"], ["system", "System"],
          ["io_type", "I/O"], ["signal_type", "Signal"], ["ex_class", "Ex"],
        ]} rows={instruments} />
      </Section>

      <Section title="Instrument cable schedule (first 10)" testid="f2-cable-table">
        <Table cols={[
          ["cable_no", "Cable"], ["from", "From"], ["to", "To"],
          ["cable_type", "Type"], ["length_m", "Length (m)"], ["tag", "Instr Tag"],
        ]} rows={cables} />
      </Section>

      <Section title="Junction box schedule" testid="f2-jb-table">
        <Table cols={[
          ["tag", "JB"], ["location", "Area"], ["type", "IS/Non-IS"],
          ["size_terminals", "Term"], ["terminals_used", "Used"],
          ["spare_pct", "Spare %"], ["ex_rating", "Ex"],
        ]} rows={jbs} />
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="f2-index-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/instrument-index/svg",
                    { instrument_index: instruments })}>
          Preview index SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="f2-index-dxf-btn"
                  onClick={() => _downloadDxf(projectId,
                    "/stage-f/instrument-index/dxf",
                    { instrument_index: instruments }, "instr-index.dxf")}>
          Download index DXF
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ F.3 · C&E / Logic ═══════════════════════════ */
function F3CeLogic({ projectId, res }) {
  const ce = res?.ce_matrix || {};
  const val = res?.ce_validation || {};
  return (
    <div className="space-y-4" data-testid="f3-ce-logic">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="C&E causes"    value={(ce.causes || []).length} testid="f3-tile-causes" />
        <StatTile label="C&E effects"   value={(ce.effects || []).length} testid="f3-tile-effects" />
        <StatTile label="Cells (X/T/A)" value={Object.keys(ce.cells || {}).length} testid="f3-tile-cells" />
        <StatTile label="Validation"    value={val.status || "-"} testid="f3-tile-status" />
      </div>

      <Section title="Validation against P&ID (F.3-F5)" testid="f3-validate">
        <ul className="text-xs text-neutral-300 list-disc pl-4 space-y-1">
          <li>Orphan causes: {(val.orphan_causes || []).length}</li>
          <li>Orphan effects: {(val.orphan_effects || []).length}</li>
          <li>Missing causes (SIS instruments on P&ID not in C&E): {(val.missing_causes || []).length}</li>
          <li>Missing effects (SDV/BDV on P&ID not in C&E): {(val.missing_effects || []).length}</li>
        </ul>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="f3-ce-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/ce-matrix/svg", { ce_matrix: ce })}>
          Preview C&E matrix SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="f3-ce-dxf-btn"
                  onClick={() => _downloadDxf(projectId,
                    "/stage-f/ce-matrix/dxf", { ce_matrix: ce },
                    "ce-matrix.dxf")}>
          Download C&E matrix DXF
        </button>
        <button className="btn btn-secondary text-xs" data-testid="f3-sis-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/sis-logic/svg",
                    { sif: { tag: "SIF-01", voting: "2oo3", SIL: 2,
                              sensors: [{ tag: "PSHH-101" }, { tag: "PSHH-102" }, { tag: "PSHH-103" }],
                              final_elements: [{ tag: "SDV-101", action: "Close" }] } })}>
          Preview SIS logic SVG
        </button>
        <button className="btn btn-secondary text-xs" data-testid="f3-shutdown-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/shutdown-key/svg")}>
          Preview shutdown key SVG
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════ F.4 · Loop Diagrams ═════════════════════════ */
function F4Loops({ projectId, res }) {
  const loops = res?.loop_diagram_first3 || [];
  return (
    <div className="space-y-4" data-testid="f4-loops">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Loop diagrams" value={res?.loop_diagram_count ?? 0} testid="f4-tile-count" />
        <StatTile label="System"         value={loops[0]?.data?.system || "-"} testid="f4-tile-system" />
        <StatTile label="Sample loop"    value={loops[0]?.loop_no || "-"} testid="f4-tile-sample" />
        <StatTile label="Primary tag"    value={loops[0]?.primary_tag || "-"} testid="f4-tile-primary" />
      </div>

      <Section title="Loop diagrams (sample first 3)" testid="f4-loop-table">
        <Table cols={[
          ["loop_no", "Loop"], ["primary_tag", "Primary tag"],
        ]} rows={loops.map(l => ({ loop_no: l.loop_no, primary_tag: l.primary_tag }))} />
      </Section>

      <div className="flex gap-2 flex-wrap">
        {loops.slice(0, 3).map((loop, i) => (
          <button key={i} className="btn btn-secondary text-xs"
                    data-testid={`f4-loop-svg-btn-${i}`}
                    onClick={() => _openSvg(projectId,
                      "/stage-f/loop/svg", { loop_data: loop.data })}>
            Preview loop {loop.loop_no} SVG
          </button>
        ))}
        {loops[0] && (
          <button className="btn btn-secondary text-xs" data-testid="f4-loop-dxf-btn"
                    onClick={() => _downloadDxf(projectId,
                      "/stage-f/loop/dxf",
                      { loop_data: loops[0].data },
                      `loop-${loops[0].loop_no}.dxf`)}>
            Download loop {loops[0].loop_no} DXF
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════ F.5 · Hookup Drawings ═══════════════════════ */
function F5Hookups({ projectId, res }) {
  const bom = res?.hookup_bom_aggregate || {};
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState("PT-LIQ");

  useEffect(() => {
    api.get(`/projects/${projectId}/stage-f/hookup/templates`)
      .then(r => setTemplates(r.data?.templates || []))
      .catch(() => {});
  }, [projectId]);

  const flat = bom.flat || [];
  const rows = flat.slice(0, 20);

  return (
    <div className="space-y-4" data-testid="f5-hookups">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Templates"     value={res?.hookup_templates_count ?? templates.length} testid="f5-tile-tpl" />
        <StatTile label="Hookups (BOM)" value={bom.hookup_count ?? 0} testid="f5-tile-hkc" />
        <StatTile label="Categories"    value={(bom.categories || []).length} testid="f5-tile-cats" />
        <StatTile label="Line items"    value={bom.total_line_items ?? 0} testid="f5-tile-items" />
      </div>

      <Section title="Hookup BOM aggregate (top 20 items)" testid="f5-bom-table">
        <Table cols={[
          ["item", "Item"], ["qty", "Qty"], ["unit", "Unit"], ["category", "Category"],
        ]} rows={rows} />
      </Section>

      <Section title="Preview hookup drawing" testid="f5-preview-select">
        <div className="flex gap-2 items-center flex-wrap">
          <select value={selected} onChange={e => setSelected(e.target.value)}
                    data-testid="f5-template-select"
                    className="bg-neutral-900 border border-neutral-700 text-xs text-neutral-200 rounded px-2 py-1">
            {templates.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="btn btn-secondary text-xs" data-testid="f5-hookup-svg-btn"
                    onClick={() => _openSvg(projectId,
                      "/stage-f/hookup/svg",
                      { template_key: selected, tag: `${selected.split("-")[0]}-100`, country: "IN" })}>
            Preview white-paper SVG
          </button>
          <button className="btn btn-secondary text-xs" data-testid="f5-hookup-dxf-btn"
                    onClick={() => _downloadDxf(projectId,
                      "/stage-f/hookup/dxf",
                      { template_key: selected, tag: `${selected.split("-")[0]}-100`, country: "IN" },
                      `hookup-${selected}.dxf`)}>
            Download DXF
          </button>
        </div>
      </Section>
    </div>
  );
}

/* ═══════════════════ F.6 · Instrument MTO ════════════════════════ */
function F6MTO({ projectId, res }) {
  const mto = res?.instrument_mto || {};
  const reqs = res?.requisitions || [];
  const comp = res?.completeness || {};
  return (
    <div className="space-y-4" data-testid="f6-mto">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Transmitters"    value={mto.totals?.transmitters ?? 0} testid="f6-tile-tx" />
        <StatTile label="Control valves"  value={mto.totals?.control_valves ?? 0} testid="f6-tile-cv" />
        <StatTile label="SDV / BDV"        value={mto.totals?.shutdown_valves ?? 0} testid="f6-tile-sdv" />
        <StatTile label="Relief valves"   value={mto.totals?.relief_valves ?? 0} testid="f6-tile-psv" />
        <StatTile label="Analyzers"        value={mto.totals?.analyzers ?? 0} testid="f6-tile-an" />
        <StatTile label="Switches"         value={mto.totals?.switches ?? 0} testid="f6-tile-sw" />
        <StatTile label="Cable meters"    value={_fmt(mto.cables?.total_meters, 0)} testid="f6-tile-cbl" />
        <StatTile label="Junction boxes"  value={mto.junction_boxes ?? 0} testid="f6-tile-jb" />
      </div>

      <Section title="Instrument requisition packages" testid="f6-reqs-table">
        <Table cols={[
          ["package", "Package"], ["category", "Category"],
          ["quantity", "Qty"], ["vendor_hint", "Vendor hint"],
          ["long_lead", "Long lead"], ["lead_time_weeks", "Lead (w)"],
        ]} rows={reqs} />
      </Section>

      <Section title="Completeness (F.6-F5)" testid="f6-complete">
        <div className="text-xs text-neutral-300 space-y-1">
          <div>Status: <b>{comp.status}</b> ({comp.percent_complete}%)</div>
          <div>Pass: {comp.count_pass} / {comp.count_total}</div>
          {(comp.missing || []).length > 0 && (
            <div className="text-amber-300">Missing: {(comp.missing || []).join(", ")}</div>
          )}
        </div>
      </Section>

      <div className="flex gap-2 flex-wrap">
        <button className="btn btn-secondary text-xs" data-testid="f6-install-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/installation-details/svg", { country: "IN" })}>
          Preview installation details
        </button>
        <button className="btn btn-secondary text-xs" data-testid="f6-loc-svg-btn"
                  onClick={() => _openSvg(projectId,
                    "/stage-f/location-plan/svg",
                    { plot_size_m: [100, 60], instrument_index: [],
                       jb_schedule: [], country: "IN" })}>
          Preview instrument location plan
        </button>
      </div>
    </div>
  );
}

/* ═════════════════════ Wrapper ════════════════════════════════ */
export default function StageFPanel({ projectId, subStage }) {
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);

  const run = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.post(`/projects/${projectId}/stage-f/run`,
                                       { plant_type: "refinery", country: "IN",
                                         plot_size_m: [100, 60] });
      setRes(r.data);
    } catch (e) {
      setRes({ error: e.message });
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="space-y-4" data-testid={`stage-f-panel-${subStage}`}>
      <div className="flex items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 p-3">
        <div>
          <div className="text-sm font-semibold text-neutral-100">Stage F · Instrumentation &amp; Control</div>
          <div className="text-xs text-neutral-400">
            Sub-stage {subStage} · powered by F.1-F.6 gap-closure engines (31 fixes)
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={run} className="btn btn-primary text-xs"
                    data-testid="stage-f-rerun-btn">Re-run pipeline</button>
        </div>
      </div>

      {loading && (
        <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-500"
              data-testid="stage-f-loading">
          Running Stage F pipeline · instrument index · I/O summary · architecture · C&E · loops · hookups · MTO…
        </div>
      )}

      {!loading && res?.error && (
        <div className="rounded-md border border-red-900 bg-red-950/50 p-4 text-xs text-red-300"
              data-testid="stage-f-error">
          Pipeline error: {res.error}
        </div>
      )}

      {!loading && !res?.error && (
        <>
          {subStage === "F.1" && <F1Architecture projectId={projectId} res={res} />}
          {subStage === "F.2" && <F2Instruments projectId={projectId} res={res} />}
          {subStage === "F.3" && <F3CeLogic     projectId={projectId} res={res} />}
          {subStage === "F.4" && <F4Loops       projectId={projectId} res={res} />}
          {subStage === "F.5" && <F5Hookups     projectId={projectId} res={res} />}
          {subStage === "F.6" && <F6MTO         projectId={projectId} res={res} />}
        </>
      )}
    </div>
  );
}
